import {
  Button,
  Controller,
  ControllerGroup,
  EButtonColorVariant,
  EControllerGroupVariant,
  ETextStyleVariant,
  EToastVariant,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { Controller as FormController, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { IBlock, ImageProps } from '@/components/templates/AppsTemplate';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { usePhoneValidatorBuilder } from '@/constants/validationRules';
import { useProfileCountry } from '@/hooks/useProfileCountry';
import { useToast } from '@/hooks/useToast';
import { sendSMS, UserType } from '@/services/appsSMS';

import RichText from '../wagtail/RichText/RichText';

interface IAppsFormProps {
  left: IBlock<string>[];
  right: IBlock<ImageProps>[];
}

interface IPhoneForm {
  phone: string;
}

const AppsForm: React.FC<IAppsFormProps> = ({ left, right }) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const form = left.find(block => block.type === 'richtext');
  const images = right.filter(block => block.type === 'image');
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState<UserType>(UserType.renter);
  const buildPhoneValidator = usePhoneValidatorBuilder();
  const profileCountry = useProfileCountry();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IPhoneForm>({ defaultValues: { phone: '' } });

  if (!form && !images.length) {
    return null;
  }

  const handleSelect = (type: string) => {
    setUserType(type as UserType);
  };

  const handleSend = async ({ phone }: IPhoneForm) => {
    const smsData = {
      app: userType,
      phone,
    };
    try {
      setLoading(true);
      await sendSMS(smsData);

      showToast(
        EToastVariant.Success,
        intl.formatMessage({ defaultMessage: 'Success!', id: 'UIDXdt' }),
        intl.formatMessage({
          defaultMessage: 'The link has been sent. Thank you!',
          id: 'js6SB8',
        }),
      );

      setLoading(false);
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
        intl.formatMessage({
          defaultMessage: 'There was an error sending your app link',
          id: 'n79xwO',
        }),
      );
      setLoading(false);
    }
  };

  return (
    <section className=" bg-primary-800">
      <div className="px-4 mx-auto md:max-w-sm lg:max-w-xl sm:px-8 lg:px-12">
        <div className="relative flex flex-col items-end text-white gap-12 lg:flex-row">
          {form && (
            <div className="w-full mt-16 lg:my-24 lg:max-w-[26rem]">
              <Text variant={ETextStyleVariant.LegalBold} className="mb-4 uppercase">
                <FormattedMessage defaultMessage="Introducing" id="WlRsiu" />
              </Text>
              <RichText
                content={form.value}
                className="flex flex-col [&>h2]:!text-white [&>h2]:!text-[40px] [&>p]:!text-[18px] lg:[&>h2]:!text-5xl [&>p]:!text-white gap-4"
                containerClassName="max-w-28"
              />
              <form onSubmit={handleSubmit(handleSend)}>
                <div className="flex justify-start my-8">
                  <ControllerGroup
                    name="Toggle User Type"
                    onChange={handleSelect}
                    defaultInputValue={userType}
                    variant={EControllerGroupVariant.Marketing}>
                    <Controller
                      ariaLabel="Guest app"
                      label="Guest app"
                      value="renter"
                      className="text-white"
                    />
                    <Controller
                      ariaLabel="Host app"
                      label="Host app"
                      value="owner"
                      className="text-white"
                    />
                  </ControllerGroup>
                </div>

                {/* Stacked: beside the country dropdown, an inline button leaves the number too narrow */}
                <div className="flex flex-col items-start gap-4">
                  <FormController
                    name="phone"
                    control={control}
                    rules={buildPhoneValidator()}
                    render={({ field: { value, onChange, ref } }) => (
                      <PhoneNumberInput
                        ref={ref}
                        name="phone"
                        label={intl.formatMessage({
                          defaultMessage: 'Phone number',
                          id: 'jdJhOL',
                        })}
                        value={value}
                        onChange={onChange}
                        userCountry={profileCountry}
                        markAsRequired
                        error={!!errors.phone}
                        helpText={errors.phone?.message}
                      />
                    )}
                  />
                  <Button
                    variant={EButtonColorVariant.Primary}
                    label={intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4' })}
                    type="submit"
                    loading={loading}
                  />
                </div>
              </form>
            </div>
          )}
          {!!images.length && (
            <div className="relative flex flex-col-reverse items-center w-full h-full">
              <ResponsiveImage
                // @ts-expect-error fixable: unchecked index access
                src={images[0].value.url}
                // @ts-expect-error fixable: unchecked index access
                alt={images[0].value.title}
                className="relative object-cover w-full h-full"
                sourceSizes={['landscape280', 'landscape375', 'landscape768']}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
AppsForm.displayName = 'AppsForm';
export default AppsForm;
